import { css } from 'styled-components';
import { FONT_WEIGHT, LINE_HEIGHT_UNIT, TEXT_CASE, TEXT_DECORATION, TYPOGRAPHY } from './eoraTypography';
import { PX_TO_REM, PxToRems, SIZES } from '../sizes/sizes';
import { breakpoints } from '../breakpoints/breakpoints';

export type FontWeights = typeof FONT_WEIGHT;
export type FontWeight = FontWeights[keyof FontWeights];

export const fontWeights: Record<FontWeight, number> = {
  [FONT_WEIGHT.REGULAR]: 400,
  [FONT_WEIGHT.MEDIUM]: 500,
  [FONT_WEIGHT.SEMI_BOLD]: 600,
  [FONT_WEIGHT.EXTRA_BOLD]: 800,
  [FONT_WEIGHT.EXTRA_LIGHT]: 200,
  [FONT_WEIGHT._65_MEDIUM]: 600,
  [FONT_WEIGHT._55_ROMAN]: 500
};

export type Units = typeof LINE_HEIGHT_UNIT;
export type Unit = Units[keyof Units];

export const units: Record<Unit, string> = {
  [LINE_HEIGHT_UNIT.PERCENT]: '%',
  [LINE_HEIGHT_UNIT.PIXELS]: 'px'
};

export type TextCases = typeof TEXT_CASE;
export type TextCase = TextCases[keyof TextCases];

export const textCases: Record<TextCase, string> = {
  [TEXT_CASE.ORIGINAL]: 'ORIGINAL',
  [TEXT_CASE.UPPER]: 'UPPER'
};

export type TextDecorations = typeof TEXT_DECORATION;
export type TextDecoration = TextDecorations[keyof TextDecorations];

export const textDecorations: Record<TextDecoration, string> = {
  [TEXT_DECORATION.NONE]: 'NONE',
  [TEXT_DECORATION.UNDERLINE]: 'UNDERLINE'
};

export type DisplayTypes = keyof typeof TYPOGRAPHY.DISPLAY;

export const displayTypes: Record<string, DisplayTypes> = {
  displayXLarge: 'X_LARGE',
  displayLarge: 'LARGE',
  displaySmall: 'SMALL'
};

export interface ITypographyCss {
  fontSize: number;
  fontFamily: string;
  fontWeight: FontWeight;
  lineHeight: number;
  lineHeightUnit: Unit;
  letterSpacing: number;
  letterSpacingUnit: Unit;
  textCase: TextCase;
  textDecoration: TextDecoration;
}

export const textCaseToString: Record<TextCase, string> = {
  ORIGINAL: 'none',
  UPPER: 'uppercase'
};

export const useEoraCss = (name: ITypographyCss, underline?: boolean, lineHeight?: number) => css`
  font-size: ${Object.keys(PX_TO_REM).includes(name.fontSize.toString()) ? PX_TO_REM[name.fontSize as keyof PxToRems] : name.fontSize + 'px'};
  font-family: ${name.fontFamily}, sans-serif;
  font-weight: ${fontWeights[name.fontWeight]};
  letter-spacing: ${name.letterSpacing / 100 + 'rem'};
  text-transform: ${textCaseToString[name.textCase]};
  text-decoration: ${underline ? textDecorations.UNDERLINE : textDecorations[name.textDecoration]};
  line-height: ${lineHeight
    ? css`
        ${lineHeight}px
      `
    : css`
        ${name.lineHeight}${units[name.lineHeightUnit]}
      `};
`;

export const useEoraMobileCss = (name: ITypographyCss) => css`
  @media only screen and (max-width: ${breakpoints[SIZES.SMALL]}) {
    font-size: ${Object.keys(PX_TO_REM).includes(name.fontSize.toString()) ? PX_TO_REM[name.fontSize as keyof PxToRems] : name.fontSize + 'px'};
    line-height: ${name.lineHeight}${units[name.lineHeightUnit]};
  }
`;
